<template>
  <div class="canvasMetric">
    <div class="metricValue">{{ formattedValue }}</div>
    <div v-if="label" class="metricLabel">{{ label }}</div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps<{
  rows: Record<string, any>[]
  columns: string[]
  displayConfig?: { format?: 'number' | 'percent' | 'currency' }
}>()

const metricData = computed(() => {
  if (!props.rows.length || !props.columns.length) return { value: 0, label: '' }
  const row = props.rows[0]
  const col = props.columns[0]
  const value = row[col]
  const labelCol = props.columns.length > 1 ? props.columns[1] : null
  const label = labelCol ? String(row[labelCol] ?? '') : ''
  return { value, label }
})

const formattedValue = computed(() => {
  const v = metricData.value.value
  const fmt = props.displayConfig?.format || 'number'

  if (v == null) return fmt === 'currency' ? '$0' : '0'

  const n = typeof v === 'number' ? v : Number(v)
  if (isNaN(n)) return String(v)

  if (fmt === 'percent') {
    // If value is already 0-100 range, display as-is; if 0-1 range, multiply
    const pct = n > 0 && n < 1 ? n * 100 : n
    if (pct === 0) return '0%'
    // Drop decimal if it's a whole number
    return (Number.isInteger(pct) ? pct.toString() : pct.toFixed(1)) + '%'
  }

  if (fmt === 'currency') {
    if (n >= 1_000_000) return '$' + (n / 1_000_000).toFixed(1) + 'M'
    if (n >= 1_000) return '$' + (n / 1_000).toFixed(1) + 'K'
    return '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  // number (default)
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K'
  if (Number.isInteger(n)) return n.toLocaleString()
  return n.toFixed(2)
})

const label = computed(() => metricData.value.label)
</script>

<style scoped>
.canvasMetric {
  padding: 24px 16px;
  text-align: center;
}
.metricValue {
  font-size: 48px;
  font-weight: 700;
  color: var(--color-text-primary, var(--text, #1a1a1a));
  line-height: 1.1;
}
.metricLabel {
  margin-top: 8px;
  font-size: 14px;
  color: var(--color-text-secondary, var(--muted, #6b7280));
}
</style>
